<template>
  <div id="pageVehiculo" @click="handleClickOutside">
    <v-container fluid>
      <!-- Filtro de rango de fechas estilo chip -->
      <v-row class="mb-2" no-gutters>
        <v-col cols="auto">
          <v-menu
            v-model="datePickerMenu"
            :close-on-content-click="false"
            offset-y
            max-width="290"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                outlined
                :color="dateRange.length === 2 ? 'primary' : 'grey'"
                class="mr-2"
                close-icon="mdi-close"
                :close="dateRange.length === 2"
                @click:close="clearDateRange"
              >
                <v-icon left small>mdi-calendar-range</v-icon>
                <span v-if="dateRange.length === 2">{{ formattedDateRange }}</span>
                <span v-else>Filtrar por fecha</span>
              </v-chip>
            </template>
            <v-date-picker 
              v-model="dateRange" 
              range
              @change="onDateRangeChange"
            >
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <!-- Sección de estadísticas -->
      <v-row class="mb-3" no-gutters>
        <!-- Vehículos totales -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="primary" size="28" class="mb-1">mdi-car-multiple</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosTotales }}</div>
              <div class="text-caption grey--text">Total</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Vehículos pendientes -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="orange" size="28" class="mb-1">mdi-car</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosPendientes }}</div>
              <div class="text-caption grey--text">Sin Bastidor</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Vehículos Sin Ubicación -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="blue" size="28" class="mb-1">mdi-car</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosCreados }}</div>
              <div class="text-caption grey--text">Sin Ubicación</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Vehículos recepcionados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="green" size="28" class="mb-1">mdi-car</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosRecepcionados }}</div>
              <div class="text-caption grey--text">Recepcionados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- En movimiento / Cesión -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="purple" size="28" class="mb-1">mdi-car-arrow-right</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosEnMovimiento }}</div>
              <div class="text-caption grey--text">En movimiento</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Vehículos libres -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="teal" size="28" class="mb-1">mdi-car-key</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosLibres }}</div>
              <div class="text-caption grey--text">Archivados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Sin Ubicación este mes -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="primary" size="28" class="mb-1">mdi-calendar-month</v-icon>
              <div class="text-h6 font-weight-bold">{{ vehiculosCreadosEsteMes }}</div>
              <div class="text-caption grey--text">Este mes</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vista principal -->
      <v-row>
        <v-col cols="12">
          <v-card class="vehicle-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-car-multiple</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Vehículos
              </v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field v-model="filter.search" label="Buscar vehículo..." prepend-inner-icon="mdi-magnify"
                  hide-details clearable rounded single-line filled dense dark @input="applyFilter"></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>

              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field ref="searchInput" v-model="filter.search" label="Buscar vehículo..."
                  prepend-inner-icon="mdi-magnify" hide-details clearable rounded single-line filled dense dark
                  @keydown.esc="showSearch = false"></v-text-field>
              </v-col>

              <!-- Menú de filtros mejorado -->
              <filter-menu title="Filtrar vehículos" :current-filter="currentFilter" @canceled="rollbackFilter()"
                @filtered="commitFilter()">
                <v-row rows="6">
                  <v-col lg="6" cols="12">
                    <v-select v-model="filter.condition" :items="filteredConditionItems" item-value="value" item-text="text"
                      label="Condición" clearable outlined dense hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-car-info" multiple>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index < 2" x-small :color="item.color" text-color="white" class="mr-1">
                          <v-icon left x-small color="white">{{ item.icon }}</v-icon>
                          {{ item.text }}
                        </v-chip>
                        <span v-if="index === 2" class="grey--text text-caption">
                          (+{{ filter.condition.length - 2 }} más)
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                    </v-select>
                    <v-text-field v-model="filter.iic_number" label="Número IIC" clearable outlined dense
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-identifier"></v-text-field>
                    <v-text-field v-model="filter.brand" label="Marca" clearable outlined dense hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-tag"></v-text-field>
                    <v-text-field v-model="filter.version" label="Versión" clearable outlined dense hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-car-side"></v-text-field>
                      <v-select 
                      v-model="filter.todo_ids" 
                      :items="todoItems" 
                      item-value="id" 
                      item-text="name"
                      label="TO-DO" 
                      clearable 
                      outlined 
                      dense 
                      hide-details="auto" 
                      class="mb-4"
                      prepend-inner-icon="mdi-format-list-checks" 
                      multiple 
                      :loading="loadingTodos"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip 
                          v-if="index < 2" 
                          x-small 
                          :color="item.button_color" 
                          :text-color="item.text_color"
                          class="mr-1"
                        >
                          <v-avatar left :color="item.button_color" size="20" style="display: flex; align-items: center; justify-content: center;">
                            <span :style="{ color: item.text_color, fontSize: '10px', fontWeight: 'bold' }">{{ getInitial(item.name) }}</span>
                          </v-avatar>
                          {{ item.name }}
                        </v-chip>
                        <span v-if="index === 2" class="grey--text text-caption">
                          (+{{ filter.todo_ids.length - 2 }} más)
                        </span>
                      </template>
                      
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar :color="item.button_color" size="32" style="display: flex; align-items: center; justify-content: center;">
                          <span :style="{ color: item.text_color, fontSize: '12px', fontWeight: 'bold' }">
                            {{ getInitial(item.name) }}
                          </span>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>

                    <!-- Campo de filtro por dealer (solo para colaboradores)-->
                    <v-select v-if="$store.getters.HAS_ROLE(['colaborator']) || this.hasDealersAsociados || hasPartnerDealers"
                      v-model="filter.dealer_id" :items="accessibleDealers" :loading="loadingAccessibleDealers"
                      item-value="id" item-text="name" label="Dealer" clearable outlined dense hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-store">
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.is_own ? 'primary' : 'grey'">
                          {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.is_own ? 'primary' : 'grey'">
                          {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                        </v-icon>
                        <span>{{ item.name }}</span>
                        <v-chip v-if="item.is_own" x-small class="ml-2" color="primary">Propio</v-chip>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col lg="6" cols="12">
                    <v-select v-model="filter.transmission" :items="transmissionItems" item-value="value"
                      item-text="text" label="Transmisión" clearable outlined dense hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-car-shift-pattern">
                      <template v-slot:selection="{ item }">
                        <v-icon left small>{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left small>{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                    </v-select>
                    <v-text-field v-model="filter.frame_number" label="Bastidor" clearable outlined dense
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-card-bulleted-outline"></v-text-field>
                    <v-text-field v-model="filter.plate_number" label="Matrícula" clearable outlined dense
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-license"></v-text-field>
                    <v-select v-model="filter.status" :items="statusItems" item-value="value" item-text="text"
                      label="Estado Vehículo" clearable outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-checkbox-marked-circle-outline" multiple>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index === 0" small :color="item.color" text-color="white" class="mr-1">
                          <v-icon left small>mdi-circle-medium</v-icon>
                          {{ item.text }}
                        </v-chip>
                        <span v-if="index === 1" class="grey--text text-caption">
                          (+{{ filter.status.length - 1 }} más)
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left small :color="item.color">mdi-circle-medium</v-icon>
                        {{ item.text }}
                      </template>
                    </v-select>
                    <v-select v-model="filter.location_id" :items="locationItems" item-value="id" item-text="name"
                      label="Ubicación" clearable outlined dense hide-details="auto" class="mb-4 mt-4"
                      prepend-inner-icon="mdi-map-marker" multiple :loading="loadingLocations">
                      <template v-slot:selection="{ item, index }">
                        <span v-if="item && item.name" class="grey--text text--darken-2">
                          {{ item.name }}
                          <span v-if="$store.getters.IS_PARTNER && item.dealer && item.dealer.name" class="ml-1 blue--text text--darken-2">
                            ({{ item.dealer.name }})
                          </span>
                          <span v-if="index < filter.location_id.length - 1">, </span>
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <div>
                          <span>{{ item.name }}</span>
                          <span v-if="$store.getters.IS_PARTNER && item.dealer_name" class="ml-1 blue--text text--darken-2">
                            ({{ item.dealer_name }})
                          </span>
                        </div>
                      </template>
                    </v-select>
                    <v-select 
                      v-model="filter.estado_proceso_id" 
                      :items="estadoProcesoItems" 
                      item-value="id" 
                      item-text="name"
                      label="Estado Proceso" 
                      clearable 
                      outlined 
                      dense 
                      hide-details="auto" 
                      class="mb-4"
                      prepend-inner-icon="mdi-file-document-outline" 
                      multiple 
                      :loading="loadingEstadosProceso"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip 
                          v-if="index < 2" 
                          x-small 
                          :color="item.button_color" 
                          :text-color="item.text_color"
                          class="mr-1"
                        >
                          <v-avatar left :color="item.button_color" size="20" style="display: flex; align-items: center; justify-content: center;">
                            <span :style="{ color: item.text_color, fontSize: '10px', fontWeight: 'bold' }">{{ getInitial(item.name) }}</span>
                          </v-avatar>
                          {{ item.name }}
                        </v-chip>
                        <span v-if="index === 2" class="grey--text text-caption">
                          (+{{ filter.estado_proceso_id.length - 2 }} más)
                        </span>
                      </template>
                      
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar :color="item.button_color" size="32" style="display: flex; align-items: center; justify-content: center;">
                          <span :style="{ color: item.text_color, fontSize: '12px', fontWeight: 'bold' }">
                            {{ getInitial(item.name) }}
                          </span>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botones de acción -->
              <v-tooltip top v-if="canCreateVehicle">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="showVehiculoForm(null)" class="mr-2" color="white">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Agregar Vehículo</span>
              </v-tooltip>

              <v-tooltip top v-if="canImportExcel">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="downloadExcelTemplate" class="mr-2" color="white" :loading="loadingTemplate">
                    <v-icon>mdi-file-excel</v-icon>
                  </v-btn>
                </template>
                <span>Descargar plantilla Excel</span>
              </v-tooltip>
              <v-tooltip top v-if="canExportVehicles">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="exportVehicles" class="mr-2" color="white" :loading="loadingExport">
                    <v-icon>mdi-microsoft-excel</v-icon>
                  </v-btn>
                </template>
                <span>Exportar vehiculos</span>
              </v-tooltip>

              <v-tooltip top v-if="canImportExcel">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="openImportDialog" class="mr-2" color="white" :loading="loadingImport">
                    <v-icon>mdi-file-upload</v-icon>
                  </v-btn>
                </template>
                <span>Importar Excel</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="refreshDataTable" v-on="on" class="mr-2" color="white" :disabled="loadingItems">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>

              <!-- Formulario modal -->
              <vehiculo-form v-if="visibleVehiculoForm" v-model="editItem" @save="updateDataTable"
                @cancel="visibleVehiculoForm = false"></vehiculo-form>
            </v-toolbar>

            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <!-- Filtros por condición -->
              <v-row class="mb-0" v-if="hasVehicleConditionPermissions">
                <v-col cols="auto">
                  <div class="d-flex py-0">
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vn') && HAS_PERMISSION('vn')" 
                      :outlined="!filter.condition.includes('new')"
                      :color="filter.condition.includes('new') ? 'green' : 'green'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('new')"
                    >
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vo') && HAS_PERMISSION('vo')" 
                      :outlined="!filter.condition.includes('used')"
                      :color="filter.condition.includes('used') ? 'amber' : 'amber'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('used')"
                    >
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('taller') && HAS_PERMISSION('taller')" 
                      :outlined="!filter.condition.includes('taller')"
                      :color="filter.condition.includes('taller') ? 'indigo' : 'indigo'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('taller')"
                    >
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>
                  </div>
                </v-col>
              </v-row>

              <!-- Filtros por estado -->
              <v-row class="align-center justify-center mt-0">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter" class="">
                    <v-chip filter outlined color="orange" small class="mr-1">
                      <v-icon left x-small>mdi-car</v-icon>
                      Sin Bastidor
                    </v-chip>
                    <v-chip filter outlined color="blue" small class="mr-1">
                      <v-icon left x-small>mdi-car</v-icon>
                      Sin Ubicación
                    </v-chip>
                    <v-chip filter outlined color="green" small class="mr-1">
                      <v-icon left x-small>mdi-car</v-icon>
                      Recepcionados
                    </v-chip>
                    <v-chip filter outlined color="purple" small class="mr-1">
                      <v-icon left x-small>mdi-truck-outline</v-icon>
                      En movimiento
                    </v-chip>
                    <v-chip filter outlined color="purple" small class="mr-1">
                      <v-icon left x-small>mdi-truck-outline</v-icon>
                      En cesión
                    </v-chip>
                    <v-chip filter outlined color="teal" small class="mr-1">
                      <v-icon left x-small>mdi-car-key</v-icon>
                      Archivados
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="primary" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>

                    <!-- Filtro de Condición -->
                    <v-chip v-if="filter.condition.includes('new')" x-small class="mr-1 mb-1" close
                      color="green" text-color="white" @click:close="filter.condition = filter.condition.filter(c => c !== 'new'); applyFilter()">
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>

                    <v-chip v-if="filter.condition.includes('used')" x-small class="mr-1 mb-1" close
                      color="amber" text-color="white" @click:close="filter.condition = filter.condition.filter(c => c !== 'used'); applyFilter()">
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>

                    <v-chip v-if="filter.condition.includes('taller')" x-small class="mr-1 mb-1" close
                      color="indigo" text-color="white" @click:close="filter.condition = filter.condition.filter(c => c !== 'taller'); applyFilter()">
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>

                    <!-- Filtro de Sin Bastidor -->
                    <v-chip v-if="filter.status && filter.status.includes('pending')" x-small class="mr-1 mb-1" close
                      color="orange" text-color="white" @click:close="filter.status = []; applyFilter()">
                      <v-icon left x-small>mdi-car</v-icon>
                      Sin Bastidor
                    </v-chip>

                    <!-- Filtro de Sin Ubicación -->
                    <v-chip v-if="filter.status && filter.status.includes('created')" x-small class="mr-1 mb-1" close
                      color="blue" text-color="white" @click:close="filter.status = []; applyFilter()">
                      <v-icon left x-small>mdi-car</v-icon>
                      Sin Ubicación
                    </v-chip>

                    <!-- Filtro de Recepcionados -->
                    <v-chip v-if="filter.status && filter.status.includes('registered')" x-small class="mr-1 mb-1" close
                      color="green" text-color="white" @click:close="filter.status = []; applyFilter()">
                      <v-icon left x-small>mdi-car</v-icon>
                      Recepcionados
                    </v-chip>

                    <!-- Filtro de En movimiento -->
                    <v-chip v-if="filter.en_movimiento" x-small class="mr-1 mb-1" close color="indigo"
                      text-color="white" @click:close="filter.en_movimiento = false; applyFilter()">
                      <v-icon left x-small>mdi-truck-outline</v-icon>
                      En movimiento
                    </v-chip>

                    <!-- Filtro de En cesión -->
                    <v-chip v-if="filter.en_cesion" x-small class="mr-1 mb-1" close color="teal"
                      text-color="white" @click:close="filter.en_cesion = false; applyFilter()">
                      <v-icon left x-small>mdi-truck-outline</v-icon>
                      En cesión
                    </v-chip>

                    <!-- Filtro de Libres -->
                    <v-chip v-if="filter.status && filter.status.includes('free')" x-small class="mr-1 mb-1" close
                      color="teal" text-color="white" @click:close="filter.status = []; applyFilter()">
                      <v-icon left x-small>mdi-car-key</v-icon>
                      Archivados
                    </v-chip>

                    <!-- Los demás filtros normales -->
                    <v-chip v-if="filter.brand" x-small class="mr-1 mb-1" close
                      @click:close="filter.brand = null; applyFilter()">
                      Marca: {{ filter.brand }}
                    </v-chip>

                    <v-chip v-if="filter.plate_number" x-small class="mr-1 mb-1" close
                      @click:close="filter.plate_number = null; applyFilter()">
                      Matrícula: {{ filter.plate_number }}
                    </v-chip>

                    <!-- Filtro de múltiples estados -->
                    <v-chip v-if="filter.status && filter.status.length > 1" x-small class="mr-1 mb-1" close
                      @click:close="filter.status = []; applyFilter()">
                      Estados: {{ filter.status.length }} seleccionado{{ filter.status.length > 1 ? 's' : '' }}
                    </v-chip>
                    
                    <!-- Filtro de TO-DO -->
                    <v-chip v-if="filter.todo_ids && filter.todo_ids.length > 0" x-small class="mr-1 mb-1" close
                      color="primary" text-color="white" @click:close="filter.todo_ids = []; applyFilter()">
                      <v-icon left x-small>mdi-format-list-checks</v-icon>
                      TO-DO: {{ filter.todo_ids.length }} seleccionado{{ filter.todo_ids.length > 1 ? 's' : '' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loadingItems" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Vista de tabla -->
            <v-card-text v-else-if="viewMode === 'table'" class="pa-0">
              <v-data-table :headers="headers" :items="VEHICULOS" :options.sync="pagination" :loading="loadingItems"
                :footer-props="footer" :key="tableId" class="elevation-0" multi-sort show-expand single-expand
                :expanded.sync="expandedItem" @item-expanded="onItemExpanded" :mobile-breakpoint="0"
                :server-items-length="totalItems" item-key="id">
                <!-- Columna de condición -->
                <template v-slot:item.condition="{ item }">
                  <v-chip 
                    small 
                    :color="item.condition === 'new' ? 'green' : item.condition === 'used' ? 'amber' : 'indigo'"
                    text-color="white"
                    label
                  >
                    <v-icon left x-small>
                      {{ item.condition === 'new' ? 'mdi-car-limousine' : item.condition === 'used' ? 'mdi-car-estate' : 'mdi-tools' }}
                    </v-icon>
                    {{ item.condition === 'new' ? 'VN' : item.condition === 'used' ? 'VO' : 'Taller' }}
                  </v-chip>
                </template>

                <!-- Columna de bastidor -->
                <template v-slot:item.frame_number="{ item }">
                  <div class="d-flex align-center text--darken-1">
                      <span v-if="item.frame_number && item.frame_number.length > 6">
                        ...{{ item.frame_number.slice(-6) }}
                      </span>
                      <span v-else>
                        {{ item.frame_number }}
                      </span>
                  </div>
                </template>

                <!-- Columna de marca/modelo -->
                <template v-slot:item.brand="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar color="primary lighten-4" size="36" class="mr-3">
                      <v-icon size="18" color="primary">mdi-car</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.brand }} {{ item.model }}<cedido-label :vehicle="item" class="ml-2" /></div>
                      <div class="caption grey--text text--darken-1">{{ item.version }}</div>
                    </div>
                  </div>
                </template>

                <!-- Columna de ubicación -->
                <template v-slot:item.location="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-map-marker</v-icon>
                    <span v-if="item.location && item.location.name">{{ item.location.name }}</span>
                    <span v-else-if="item.previous_location">
                      {{ item.previous_location }}
                      <span class="caption font-italic grey--text ml-1">(ubicación previa)</span>
                    </span>
                    <span v-else>No disponible</span>
                  </div>
                </template>

                <!-- Columna de dealer (solo para colaboradores) -->
                <template v-slot:item.dealer="{ item }"
                  v-if="$store.getters.HAS_ROLE(['colaborator']) || this.hasDealersAsociados || hasPartnerDealers">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <img v-if="item.dealer && item.dealer.imageUrl && item.dealer.imageUrl.url"
                        :src="item.dealer.imageUrl.url" alt="Logo del dealer">
                      <v-icon v-else small color="grey">mdi-store</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.dealer ? item.dealer.name : 'N/A' }}</div>
                      <div class="caption grey--text text--darken-1">{{ item.dealer ? item.dealer.city : '' }}</div>
                    </div>
                  </div>
                </template>

                <!-- Columna de TO-DO -->
                <template v-slot:item.todos="{ item }">
                  <div class="d-flex flex-wrap justify-center">
                    <v-tooltip
                      v-for="todo in getActiveTodos(item.todos).slice(0, 3)"
                      :key="todo.id"
                      top
                    >
                      <template v-slot:activator="{ on }">
                        <v-avatar
                          :color="todo.button_color"
                          size="28"
                          class="ma-1 cursor-pointer"
                          v-on="on"
                          @click="closeTodo(todo, item)"
                        >
                          <span
                            class="white--text text-caption font-weight-bold"
                            :style="{ color: todo.text_color }"
                          >
                            {{ getInitial(todo.name) }}
                          </span>
                        </v-avatar>
                      </template>
                      <span>{{ todo.name }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="getActiveTodos(item.todos).length > 3" top>
                      <template v-slot:activator="{ on }">
                        <v-avatar
                          color="grey"
                          size="28"
                          class="ma-1"
                          v-on="on"
                        >
                          <span class="white--text text-caption font-weight-bold">
                            +{{ getActiveTodos(item.todos).length - 3 }}
                          </span>
                        </v-avatar>
                      </template>
                      <span>{{ getActiveTodos(item.todos).slice(3).map(t => t.name).join(', ') }}</span>
                    </v-tooltip>
                    <span v-if="getActiveTodos(item.todos).length === 0" class="text-caption grey--text">
                      Sin tareas
                    </span>
                  </div>
                </template>

                <!-- Columna de Estado de Procesos -->
                <template v-slot:item.currentVehicleLocationEstadoProceso="{ item }">
                  <div class="d-flex flex-wrap justify-center">
                    <v-chip 
                      v-if="item.currentVehicleLocationEstadoProceso" 
                      :color="item.currentVehicleLocationEstadoProceso.estadoProceso.button_color" 
                      small 
                      dark 
                      label
                      @click="openEstadoProcesoDialog(item)"
                      style="cursor: pointer;"
                    >
                      <span :style="{ color: item.currentVehicleLocationEstadoProceso.estadoProceso.text_color }">
                        {{ item.currentVehicleLocationEstadoProceso.estadoProceso.name }}
                      </span>
                    </v-chip>
                    <v-chip
                      v-else
                      color="grey"
                      small
                      dark
                      label
                      @click="openEstadoProcesoDialog(item)"
                      style="cursor: pointer;"
                    >
                      Sin estado
                    </v-chip>
                  </div>
                </template>

                <!-- Columna de estado -->
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getStatusColor(item)" small dark label>
                    {{ getStatusText(item) }}
                  </v-chip>
                </template>

                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-buttons">
                    <v-tooltip top
                      v-if="canEditVehicleOfDealer(item)">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="primary" @click.stop="showVehiculoForm(item)" v-on="on">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="info" :to="{ name: 'VehiculoDetail', params: { id: item.id } }"
                          v-on="on" @click.stop>
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip>

                    <v-tooltip top
                      v-if="canDeleteVehicleOfDealer(item)">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="red" @click.stop="confirmDeleteVehiculo(item)" v-on="on">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Eliminar</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Vista expandida -->
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-4">
                    <v-card flat outlined class="pa-3">
                      <v-card-title class="text-subtitle-1 pa-0 mb-3">
                        <v-icon left color="primary">mdi-information-outline</v-icon>
                        Información detallada
                      </v-card-title>

                      <v-row>
                        <v-col cols="12" sm="6" md="3">
                          <div class="caption grey--text">Bastidor</div>
                          <div class="subtitle-2">{{ item.frame_number || 'No especificado' }}</div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="caption grey--text">Motor</div>
                          <div class="subtitle-2">{{ item.engine || 'No especificado' }}</div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="caption grey--text">Combustible</div>
                          <div class="subtitle-2">{{ item.fuel || 'No especificado' }}</div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="caption grey--text">Transmisión</div>
                          <div class="subtitle-2">{{ item.transmission === 'auto' ? 'Automática' : 'Manual' }}</div>
                        </v-col>
                      </v-row>

                      <v-divider class="my-3"></v-divider>

                      <v-card-title class="text-subtitle-1 pa-0 mb-3">
                        <v-icon left color="primary">mdi-history</v-icon>
                        Último movimiento
                      </v-card-title>

                      <template v-if="loadingMovements[item.id]">
                        <div class="text-center pa-3">
                          <v-progress-circular indeterminate color="primary" size="24" width="2"></v-progress-circular>
                          <div class="mt-2 caption">Cargando información...</div>
                        </div>
                      </template>

                      <template v-else-if="item.last_movement?.error">
                        <div class="error--text pa-2">
                          {{ item.last_movement.error }}
                        </div>
                      </template>

                      <template v-else-if="item.last_movement">
                        <v-row>
                          <v-col cols="12" sm="6" md="3">
                            <div class="caption grey--text">Origen</div>
                            <div class="subtitle-2">{{ sourceLocation || 'No especificado' }}
                            </div>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <div class="caption grey--text">Destino</div>
                            <div class="subtitle-2">{{ destinationLocation || 'No especificado'
                              }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <div class="caption grey--text">Transporte</div>
                            <div class="subtitle-2">{{ driver || 'Driver: No especificado' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <div class="caption grey--text">Fecha</div>
                            <div class="subtitle-2">{{ formatDate(item.last_movement.updated_at) }}</div>
                          </v-col>
                        </v-row>

                        <v-row class="mt-2">
                          <v-col cols="12" class="pt-0">
                            <v-btn small outlined color="primary"
                              :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.last_movement.id } }"
                              class="mt-2">
                              <v-icon left small>mdi-eye</v-icon>
                              Ver movimiento
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>

                      <template v-else>
                        <div class="grey--text pa-2">
                          No hay registros de movimientos para este vehículo.
                        </div>
                      </template>
                    </v-card>
                  </td>
                </template>

                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron vehículos</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      Intenta con otros filtros o agrega un nuevo vehículo
                    </p>
                    <v-btn color="primary" outlined @click="showVehiculoForm(null)" v-if="canCreateVehicle">
                      <v-icon left>mdi-plus</v-icon>
                      Agregar vehículo
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <v-row>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="item in VEHICULOS" :key="item.id">
                  <v-card outlined hover class="vehicle-item-card h-100">
                    <v-card-text class="pa-4">
                      <!-- Header de tarjeta con estado -->
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div class="d-flex flex-wrap">
                          <v-chip :color="getStatusColor(item)" small dark label class="mr-1 mb-1">
                            {{ getStatusText(item) }}
                          </v-chip>
                          <v-chip 
                            v-if="item.currentVehicleLocationEstadoProceso" 
                            :color="item.currentVehicleLocationEstadoProceso.estadoProceso.button_color" 
                            small 
                            dark 
                            label
                            class="mb-1"
                            @click="openEstadoProcesoDialog(item)"
                            style="cursor: pointer;"
                          >
                            <v-icon left x-small>mdi-list-status</v-icon>
                            <span :style="{ color: item.currentVehicleLocationEstadoProceso.estadoProceso.text_color }">
                              {{ item.currentVehicleLocationEstadoProceso.estadoProceso.name }}
                            </span>
                          </v-chip>
                          <v-chip
                            v-else
                            color="grey"
                            small
                            dark
                            label
                            class="mb-1"
                            @click="openEstadoProcesoDialog(item)"
                            style="cursor: pointer;"
                          >
                            <v-icon left x-small>mdi-list-status</v-icon>
                            Sin estado
                          </v-chip>
                        </div>
                        <v-menu bottom left offset-y transition="slide-y-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small v-bind="attrs" v-on="on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item v-if="canEditVehicleOfDealer(item)" @click="showVehiculoForm(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Editar</v-list-item-title>
                            </v-list-item>
                            <v-list-item :to="{ name: 'VehiculoDetail', params: { id: item.id } }">
                              <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-eye</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Ver detalles</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canDeleteVehicleOfDealer(item)" @click="confirmDeleteVehiculo(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="red">mdi-delete</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="red--text">Eliminar</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                      <!-- Avatar y detalles principales -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" :color="`${getStatusColor(item)} lighten-4`" class="mr-3">
                          <v-icon size="32" :color="getStatusColor(item)">mdi-car</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="d-flex align-center mb-1">
                            <span class="subtitle-1 font-weight-medium mr-2">
                              {{ item.brand }} {{ item.model }}<cedido-label :vehicle="item" class="ml-2" />
                            </span>
                            <v-chip 
                              x-small 
                              :color="item.condition === 'new' ? 'green' : item.condition === 'used' ? 'amber' : 'indigo'"
                              text-color="white"
                              label
                            >
                              {{ item.condition === 'new' ? 'VN' : item.condition === 'used' ? 'VO' : 'Taller' }}
                            </v-chip>
                          </div>
                          <div class="caption grey--text text--darken-1">
                            {{ item.version }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-license</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.plate_number || 'Sin matrícula' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-identifier</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.frame_number || 'Sin bastidor' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-card-account-details</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.iic_number || 'Sin IIC' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-palette</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.color || 'No especificado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-map-marker</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <span v-if="item.location && item.location.name">{{ item.location.name }}</span>
                              <span v-else-if="item.previous_location">
                                {{ item.previous_location }}
                                <span class="caption font-italic grey--text">(ubicación previa)</span>
                              </span>
                              <span v-else>No disponible</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-format-list-checks</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <div class="d-flex flex-wrap align-center">
                                <v-tooltip
                                  v-for="todo in item.todos && item.todos.length > 0 ? item.todos.slice(0, 3) : []"
                                  :key="todo.id"
                                  top
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-avatar
                                      :color="todo.button_color"
                                      size="28"
                                      class="mr-1"
                                      v-bind="attrs"
                                      v-on="on"
                                      style="display: inline-flex; align-items: center; justify-content: center;"
                                    >
                                      <span :style="{ color: todo.text_color, fontSize: '12px', fontWeight: 'bold' }">
                                        {{ getInitial(todo.name) }}
                                      </span>
                                    </v-avatar>
                                  </template>
                                  <span>{{ todo.name }}</span>
                                </v-tooltip>
                                <v-tooltip v-if="item.todos && item.todos.length > 3" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip x-small v-bind="attrs" v-on="on" class="ml-1">
                                      +{{ item.todos.length - 3 }}
                                    </v-chip>
                                  </template>
                                  <span>{{ item.todos.length - 3 }} más</span>
                                </v-tooltip>
                                <span v-if="!item.todos || item.todos.length === 0" class="text-caption grey--text">
                                  Sin tareas
                                </span>
                              </div>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>

                    <v-divider></v-divider>

                    <!-- Acciones -->
                    <v-card-actions class="d-flex flex-wrap pa-2">
                      <v-btn text small color="primary" @click="openTransportForm(item)" v-if="canMov(item)"
                        class="flex-grow-1 ma-1">
                        <v-icon left small>mdi-car-arrow-right</v-icon>
                        Pet. movimiento
                      </v-btn>
                      <v-btn text small color="primary" @click="openTransportFormRecepcionar(item)"
                        v-if="canRecepcionar(item)" class="flex-grow-1 ma-1">
                        <v-icon left small>mdi-car-arrow-right</v-icon>
                        Recepcionar
                      </v-btn>
                      <v-btn text small color="primary" @click="openAssignmentForm(item)" v-if="canCesion(item)"
                        class="flex-grow-1 ma-1">
                        <v-icon left small>mdi-account-arrow-right</v-icon>
                        Crear cesión
                      </v-btn>
                      <v-btn text small color="success" @click="openEstadoProcesoDialog(item)"
                        class="flex-grow-1 ma-1">
                        <v-icon left small>mdi-list-status</v-icon>
                        Cambiar proceso
                      </v-btn>
                      <v-btn text small color="primary" :to="{ name: 'VehiculoDetail', params: { id: item.id } }"
                        class="flex-grow-1 ma-1">
                        <v-icon left small>mdi-eye</v-icon>
                        Ver detalles
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sin resultados -->
              <v-row v-if="!VEHICULOS.length && !loadingItems">
                <v-col cols="12" class="text-center pa-6">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron vehículos</h3>
                  <p class="subtitle-1 grey--text mb-4">
                    Intenta con otros filtros o agrega un nuevo vehículo
                  </p>
                  <v-btn color="primary" outlined @click="showVehiculoForm(null)" v-if="canCreateVehicle">
                    <v-icon left>mdi-plus</v-icon>
                    Agregar vehículo
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Paginación -->
              <v-row v-if="VEHICULOS.length > 0">
                <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                  <div class="text-center d-flex align-center mr-4">
                    <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                    <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                      hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                  </div>

                  <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                    :total-visible="7" circle @input="onPageChange"></v-pagination>

                  <span class="text-body-2 grey--text text--darken-1 ml-4">
                    {{ paginationText }}
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diálogo de confirmación mejorado -->
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon color="error" left>mdi-alert-circle</v-icon>
          Confirmar Eliminación
        </v-card-title>

        <v-card-text class="py-4">
          <v-row>
            <v-col cols="auto" class="pr-0">
              <v-avatar color="error" size="48" class="mr-4">
                <v-icon dark>mdi-car-off</v-icon>
              </v-avatar>
            </v-col>

            <v-col>
              <p class="mb-2">¿Estás seguro de que deseas eliminar este vehículo?</p>
              <p class="subtitle-1 font-weight-medium">
                {{ selectedVehiculo.brand }} {{ selectedVehiculo.model }} {{ selectedVehiculo.version }}
              </p>
              <p class="caption grey--text mt-1" v-if="selectedVehiculo.plate_number">
                Matrícula: {{ selectedVehiculo.plate_number }}
              </p>
              <p class="caption grey--text" v-if="selectedVehiculo.frame_number">
                Bastidor: {{ selectedVehiculo.frame_number }}
              </p>
            </v-col>
          </v-row>

          <p class="caption mt-3 red--text">Esta acción no se puede deshacer.</p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDelete = false" class="mr-2">Cancelar</v-btn>
          <v-btn color="error" text @click="deleteVehiculo" :loading="loadingDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo de importación de Excel -->
    <v-dialog v-model="importDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline primary--text">
          <v-icon color="primary" left>mdi-file-upload</v-icon>
          Importar vehículos desde Excel
        </v-card-title>

        <v-card-text class="py-4">
          <!-- Selector de archivo -->
          <div v-if="!importResult">
            <v-file-input
              v-model="importFile"
              label="Seleccionar archivo Excel"
              accept=".xlsx,.xls"
              prepend-icon="mdi-file-excel"
              outlined
              :loading="loadingImport"
              show-size
              clearable
            ></v-file-input>
            
            <v-alert type="info" outlined class="mt-3">
              <strong>Instrucciones:</strong>
              <ul class="mt-2">
                <li>Descarga primero la plantilla Excel usando el botón correspondiente</li>
                <li>Completa la información de los vehículos en la plantilla</li>
                <li>Sube el archivo completado aquí</li>
              </ul>
            </v-alert>
          </div>

          <!-- Resultado de importación -->
          <div v-if="importResult">
            <v-alert 
              :type="importResult.summary.error_count > 0 ? 'warning' : 'success'" 
              outlined
              class="mb-4"
            >
              <div class="text-h6 mb-2">{{ importResult.message }}</div>
              <v-row>
                <v-col cols="6" sm="3">
                  <div class="text-center">
                    <div class="text-h5 font-weight-bold">{{ importResult.summary.total_rows }}</div>
                    <div class="caption">Total</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-center">
                    <div class="text-h5 font-weight-bold success--text">{{ importResult.summary.success_count }}</div>
                    <div class="caption">Exitosos</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-center">
                    <div class="text-h5 font-weight-bold error--text">{{ importResult.summary.error_count }}</div>
                    <div class="caption">Errores</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-center">
                    <div class="text-h5 font-weight-bold warning--text">{{ importResult.summary.update_count }}</div>
                    <div class="caption">Actualizados</div>
                  </div>
                </v-col>
              </v-row>
            </v-alert>

            <v-btn
              color="primary"
              outlined
              block
              @click="downloadImportResult"
              :loading="loadingTemplate"
              class="mb-3"
            >
              <v-icon left>mdi-download</v-icon>
              Descargar resultado detallado
            </v-btn>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            text 
            @click="closeImportDialog"
            :disabled="loadingImport"
          >
            {{ importResult ? 'Cerrar' : 'Cancelar' }}
          </v-btn>
          <v-btn 
            v-if="!importResult"
            color="primary" 
            @click="importExcel"
            :disabled="!importFile || loadingImport"
            :loading="loadingImport"
          >
            <v-icon left>mdi-upload</v-icon>
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transport-form v-if="selectedVehicleForTransport" :visible="transportFormVisible"
      :status="selectedVehicleForTransport" :is-assignment="isAssignmentMode" :is-movement="isMovementMode"
      :is-recepcionar="isRecepcionarMode" @close="closeTransportForm" @submit="handleTransportSubmit" />
      
    <!-- Diálogo para cambiar estado de proceso -->
    <vehicle-estado-proceso-dialog
      v-model="estadoProcesoDialogVisible"
      :vehicle="selectedVehicleForEstado"
      @estado-updated="onEstadoProcesoUpdated"
    />
  </div>
</template>
<script>
import VehiculoForm from './VehiculoForm'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import FilterMenu from '../../components/FilterMenu'
import 'echarts/lib/chart/pie'
import VehiculoService from '@/services/VehiculoService'
import LocationService from '@/services/LocationService'
import DealerService from '@/services/DealerService'
import DriverService from '../../services/DriverService'
import TransportForm from '../../components/TransportForm'
import UserService from '../../services/UserService'
import GruaService from '../../services/GruaService'
import TodoCategoryService from '../../services/TodoCategoryService'
import EstadoProcesoService from '../../services/EstadoProcesoService'
import VehicleTodoService from '../../services/VehicleTodoService'
import VehicleEstadoProcesoDialog from '../../components/VehicleEstadoProcesoDialog'
// import UserService from '../../services/UserService'
// import VChart from 'vue-echarts'

import CedidoLabel from '../../components/CedidoLabel'

export default {
  components: {
    CedidoLabel,
    VehiculoForm,
    FilterMenu,
    TransportForm,
    VehicleEstadoProcesoDialog
  },

  data: () => ({
    // Vista y carga
    manualViewMode: null, // Para almacenar vista manual del usuario
    loadingItems: true, // Inicializar en true para mostrar skeleton al cargar
    loadingDelete: false,
    loadingTemplate: false,
    loadingExport: false,
    loadingImport: false,
    totalItems: 0,
    tableId: 0,
    showSearch: false,
    sourceLocation: '',
    destinationLocation: '',
    driver: '',

    // Filtro de rango de fechas
    dateRange: [],
    datePickerMenu: false,

    // Diálogo de importación
    importDialog: false,
    importFile: null,
    importResult: null,

    // Estadísticas
    vehiculosTotales: 0,
    vehiculosPendientes: 0,
    vehiculosCreados: 0,
    vehiculosRecepcionados: 0,
    vehiculosEnMovimiento: 0,
    vehiculosLibres: 0,
    vehiculosCreadosEsteMes: 0,
    transportFormVisible: false,
    selectedVehicleForTransport: null,
    isAssignmentMode: false,
    isMovementMode: false,
    isRecepcionarMode: false,

    // Estado de proceso dialog
    estadoProcesoDialogVisible: false,
    selectedVehicleForEstado: null,

    // Dealers accesibles (para colaboradores)
    accessibleDealers: [],
    loadingAccessibleDealers: false,

    // Datatable
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Vehículos por página:',
      pageText: '{0}-{1} de {2}'
    },

    // currentFilter se mantiene en data para la funcionalidad del FilterMenu
    currentFilter: {
      search: null,
      status: [],
      condition: [],
      dealer_id: null,
      location_id: [],
      todo_ids: [],
      estado_proceso_id: [],
      iic_number: null,
      brand: null,
      model: null,
      version: null,
      transmission: null,
      frame_number: null,
      plate_number: null
    },

    // Opciones para ubicaciones
    locationItems: [],
    loadingLocations: false,
    
    // Opciones para TO-DO
    todoItems: [],
    loadingTodos: false,
    
    // Opciones para estados de proceso
    estadoProcesoItems: [],
    loadingEstadosProceso: false,
    async loadLocations() {
      this.loadingLocations = true
      try {
        const hasPartners = this.$store.getters.IS_PARTNER

        if (hasPartners) {
          const response = await LocationService.getPartnerLocations()
          // La respuesta viene como response.data.data.locations (hay un data anidado)
          this.locationItems = response.data?.data?.locations || []
        } else {
          const response = await LocationService.get({ 'per-page': 1000 })
          // Soporta respuesta paginada o directa
          let locations = []
          if (response.data && response.data.data && Array.isArray(response.data.data)) {
            locations = response.data.data
          }
          this.locationItems = locations
        }
      } catch (error) {
        this.$toast && this.$toast.error('Error al cargar ubicaciones')
      } finally {
        this.loadingLocations = false
      }
    },
    
    async loadTodos () {
      this.loadingTodos = true
      try {
        const response = await TodoCategoryService.get()
        // Soporta respuesta paginada o directa
        let todos = []
        if (response.data && response.data.data && Array.isArray(response.data.data.data)) {
          todos = response.data.data.data
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          todos = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          todos = response.data
        }
        this.todoItems = todos
      } catch (error) {
        this.$toast && this.$toast.error('Error al cargar TO-DO')
      } finally {
        this.loadingTodos = false
      }
    },
    
    async loadEstadosProceso () {
      this.loadingEstadosProceso = true
      try {
        const response = await EstadoProcesoService.get()
        // Soporta respuesta paginada o directa
        let estados = []
        if (response.data && response.data.data && Array.isArray(response.data.data.data)) {
          estados = response.data.data.data
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          estados = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          estados = response.data
        }
        this.estadoProcesoItems = estados
      } catch (error) {
        this.$toast && this.$toast.error('Error al cargar estados de proceso')
      } finally {
        this.loadingEstadosProceso = false
      }
    },

    // Opciones para filtros
    statusItems: [
      { text: 'Sin Bastidor', value: 'pending', color: 'orange' },
      { text: 'Sin Ubicación', value: 'created', color: 'blue' },
      { text: 'Recepcionado', value: 'registered', color: 'green' },
      { text: 'En movimiento/cesión', value: 'delivered', color: 'purple' },
      { text: 'Archivados', value: 'free', color: 'teal' }
    ],
    conditionItems: [
      { text: 'VN', value: 'new', icon: 'mdi-car-limousine', color: 'green' },
      { text: 'VO', value: 'used', icon: 'mdi-car-estate', color: 'amber' },
      { text: 'Taller', value: 'taller', icon: 'mdi-tools', color: 'indigo' }
    ],
    transmissionItems: [
      { text: 'Automática', value: 'auto', icon: 'mdi-car-automatic' },
      { text: 'Manual', value: 'manual', icon: 'mdi-car-manual' }
    ],

    // Formulario
    visibleVehiculoForm: false,
    editItem: null,
    expandedItem: [],
    loadingMovements: {},

    // Diálogo de confirmación
    dialogDelete: false,
    selectedVehiculo: {}
  }),

  computed: {
    ...mapGetters(['VEHICULOS', 'CURRENT_USER_ROLES', 'CURRENT_USER', 'HAS_PERMISSION', 'DEALER_HAS_TYPE', 'HAS_PARTNER_PERMISSION', 'HAS_PERMISSION_ANYWHERE']),
    
    // Formatear el rango de fechas para mostrar en el chip
    formattedDateRange() {
      if (this.dateRange && this.dateRange.length === 2) {
        const sortedDates = [...this.dateRange].sort()
        const formatDate = (dateStr) => {
          const date = new Date(dateStr)
          return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`
        }
        return `${formatDate(sortedDates[0])} - ${formatDate(sortedDates[1])}`
      }
      return ''
    },
    
    hasDealersAsociados() {
      const user = this.$store.getters.CURRENT_USER
      if (!user || !user.dealer) return false
      return (
        (Array.isArray(user.dealers_asociados) && user.dealers_asociados.length > 0) ||
        !!user.dealer.is_colaborador
      )
    },

    // Permisos de vehículos
    canCreateVehicle() {
      return this.HAS_PERMISSION('crear_vehiculo') || this.HAS_PERMISSION_ANYWHERE('crear_vehiculo')
    },

    canEditVehicle() {
      return this.HAS_PERMISSION('editar_vehiculo')
    },

    canDeleteVehicle() {
      return this.HAS_PERMISSION('eliminar_vehiculo')
    },

    canImportExcel() {
      return this.HAS_PERMISSION('importacion_masiva_excel')
    },
    canExportVehicles() {
      return this.HAS_PERMISSION('exportar_vehiculos')
    },

    // Verificar si el usuario tiene al menos un permiso de condición de vehículo
    hasVehicleConditionPermissions() {
      // Mostrar filtros si el usuario tiene al menos 1 tipo de vehículo activo en el dealer
      const types = ['vn', 'vo', 'taller']
      const count = types.reduce((acc, type) => {
        // Primero verificar si el dealer tiene el tipo, luego verificar permisos
        return acc + (this.DEALER_HAS_TYPE(type) && this.HAS_PERMISSION(type) ? 1 : 0)
      }, 0)
      return count > 1 // Cambiar de > 1 a >= 1 para mostrar con al menos 1 tipo
    },

    // Filtrar las opciones de condición según los tipos del dealer y permisos del usuario
    filteredConditionItems() {
      return this.conditionItems.filter(item => {
        let dealerHasType = false
        let userHasPermission = false
        
        // Determinar qué verificar según el valor del item
        if (item.value === 'new') {
          dealerHasType = this.DEALER_HAS_TYPE('vn')
          userHasPermission = this.HAS_PERMISSION('vn')
        } else if (item.value === 'used') {
          dealerHasType = this.DEALER_HAS_TYPE('vo')
          userHasPermission = this.HAS_PERMISSION('vo')
        } else if (item.value === 'taller') {
          dealerHasType = this.DEALER_HAS_TYPE('taller')
          userHasPermission = this.HAS_PERMISSION('taller')
        }
        
        // Solo mostrar si AMBAS condiciones son verdaderas
        const shouldShow = dealerHasType && userHasPermission
        return shouldShow
      })
    },

    viewMode: {
      get() {
        // Si el usuario cambió manualmente la vista, usar esa
        if (this.manualViewMode) {
          return this.manualViewMode
        }
        // Detectar dispositivo y retornar vista por defecto
        if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp) {
          return 'table'
        }
        return 'cards'
      },
      set(value) {
        // Permitir cambio manual de vista
        this.manualViewMode = value
      }
    },
    pagination: {
      get: function () {
        return this.$store.getters.VEHICULOS_PAG
      },
      set: function (value) {
        this.$store.commit('SET_VEHICULOS_PAG', value)
      }
    },
    filter: {
      get: function () {
        return this.$store.getters.VEHICULOS_FILTERS
      },
      set: function (value) {
        this.$store.commit('SET_VEHICULOS_FILTERS', value)
      }
    },
    quickFilter: {
      get: function () {
        return this.$store.getters.VEHICULOS_QUICK_FILTER
      },
      set: function (value) {
        this.$store.commit('SET_VEHICULOS_QUICK_FILTER', value)
      }
    },
    hasPartnerDealers() {
      return this.$store.getters.IS_PARTNER || this.$store.getters.IN_SWITCHED_SESSION
    },
    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },
    searchTerm () {
      return this.filter.search
    },
    totalPages () {
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage)
    },
    hasActiveFilters () {
      return Object.entries(this.filter).some(([key, value]) => {
        // Manejar filtros booleanos específicamente
        if (key === 'en_movimiento' || key === 'en_cesion') {
          return value === true
        }
        // Manejar arrays
        if (Array.isArray(value)) {
          return value.length > 0
        }
        // Manejar otros valores
        return value !== null && value !== undefined && value !== '' && 
          value !== false && !['expand'].includes(value)
      })
    },
    headers () {
      const baseHeaders = [
        { text: '', value: 'data-table-expand' },
        { text: 'Condición', value: 'condition', sortable: true, align: 'center', width: '120px' },
        { text: 'Vehículo', value: 'brand', sortable: true },
        { text: 'Bastidor', value: 'frame_number' },
        { text: 'Matrícula', value: 'plate_number' },
        { text: 'Número IIC', value: 'iic_number' },
        { text: 'Ubicación', value: 'location', sortable: false },
        { text: 'TO-DO', value: 'todos', sortable: false, align: 'center', width: '150px' },
        { text: 'Estado Proceso', value: 'currentVehicleLocationEstadoProceso', sortable: false, align: 'center', width: '150px' },
        { text: 'Estado Vehículo', value: 'status', sortable: true, align: 'center' }
      ]

      // Si el usuario es colaborador, agregar columna de Dealer
      if (this.$store.getters.HAS_ROLE(['colaborator']) || this.hasDealersAsociados || this.hasPartnerDealers) {
        baseHeaders.splice(6, 0, { text: 'Dealer', value: 'dealer', sortable: false })
      }

      // Agregar columna de acciones al final
      baseHeaders.push({ text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '120px' })

      return baseHeaders
    }
  },

  methods: {
    // Filtrar solo TODOs activos (status: created)
    getActiveTodos(todos) {
      if (!todos || !Array.isArray(todos)) return []
      return todos.filter(todo => todo.status === 'created')
    },

    async closeTodo(todo, vehicle) {
      try {
        const confirm = await this.$confirm(
          `¿Está seguro que desea cerrar la tarea "${todo.name}" para el vehículo ${vehicle.plate_number || vehicle.iic_number}?`,
          {
            title: 'Cerrar Tarea',
            buttonTrueText: 'Sí, cerrar',
            buttonFalseText: 'Cancelar'
          }
        )

        if (confirm) {
          await VehicleTodoService.close(vehicle.id, todo.id)
          this.$toast.success(`Tarea "${todo.name}" cerrada exitosamente`)
          
          // Recargar los vehículos para actualizar la vista
          this.refreshDataTable()
        }
      } catch (error) {
        if (error) { // Solo mostrar error si no es por cancelación
          this.$toast.error('Error al cerrar la tarea')
        }
      }
    },

    getInitial(name) {
      if (!name || name.length === 0) return '?'
      return name.charAt(0).toUpperCase()
    },
    canEditVehicleOfDealer(vehicle) {
      if (!vehicle || !vehicle.dealer) return false
      const dealerId = vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      // Es su propio dealer y tiene permiso normal
      if (dealerId === myDealerId) {
        return this.HAS_PERMISSION('editar_vehiculo')
      }

      // Es un dealer partner — verificar permiso cruzado
      return this.$store.getters.HAS_PARTNER_PERMISSION({
        dealerId: dealerId,
        permission: 'editar_vehiculo'
      })
    },

    canDeleteVehicleOfDealer(vehicle) {
      if (!vehicle || !vehicle.dealer) return false
      // P-03: mientras el vehículo esté cedido a otro dealer, nadie puede
      // eliminarlo. El propietario lo recupera cuando lo devuelvan.
      if (vehicle.cedido_meta && vehicle.cedido_meta.is_cedido) return false

      const dealerId = vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      if (dealerId === myDealerId) {
        return this.HAS_PERMISSION('eliminar_vehiculo')
      }

      return this.$store.getters.HAS_PARTNER_PERMISSION({
        dealerId: dealerId,
        permission: 'eliminar_vehiculo'
      })
    },
    
    async loadAccessibleDealers () {
      // if (!this.$store.getters.HAS_ROLE(['colaborator']) || !this.hasDealersAsociados) {
      //   return // Solo cargar para colaboradores
      // }

      try {
        this.loadingAccessibleDealers = true
        const response = await DealerService.getMyAccessibleDealers()
        
        // Verificar la estructura de la respuesta
        let dealersArray = []
        if (response.data && response.data.data && Array.isArray(response.data.data.data)) {
          dealersArray = response.data.data.data
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          dealersArray = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          dealersArray = response.data
        }
        
        this.accessibleDealers = dealersArray
      } catch (error) {
        this.$toast.error('Error al cargar la lista de dealers')
      } finally {
        this.loadingAccessibleDealers = false
      }
    },

    loadData () {
      this.loadingItems = true
      
      // Construir parámetros de filtro
      let filterParams = { ...this.filter }
      // Si location_id es array y tiene elementos, enviar como array, si está vacío, eliminar
      if (Array.isArray(filterParams.location_id)) {
        if (!filterParams.location_id.length) {
          delete filterParams.location_id
        }
      }
      // Si status es array y tiene elementos, enviar como array, si está vacío, eliminar
      if (Array.isArray(filterParams.status)) {
        if (!filterParams.status.length) {
          delete filterParams.status
        }
      }
      
      // Si todo_ids es array y tiene elementos, enviar como array, si está vacío, eliminar
      if (Array.isArray(filterParams.todo_ids)) {
        if (!filterParams.todo_ids.length) {
          delete filterParams.todo_ids
        }
      }
      
      // Si estado_proceso_id es array y tiene elementos, enviar como array, si está vacío, eliminar
      if (Array.isArray(filterParams.estado_proceso_id)) {
        if (!filterParams.estado_proceso_id.length) {
          delete filterParams.estado_proceso_id
        }
      }
      
      // Si condition es array y tiene elementos, enviar como array, si está vacío, eliminar
      if (Array.isArray(filterParams.condition)) {
        if (!filterParams.condition.length) {
          delete filterParams.condition
        }
      }
      
      // Manejar rango de fechas
      if (this.dateRange && this.dateRange.length === 2) {
        // Convertir las fechas a timestamps Unix
        const sortedDates = [...this.dateRange].sort()
        const startDate = new Date(sortedDates[0])
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(sortedDates[1])
        endDate.setHours(23, 59, 59, 999)
        
        filterParams.created_at_from = Math.floor(startDate.getTime() / 1000)
        filterParams.created_at_to = Math.floor(endDate.getTime() / 1000)
      }
      
      // Manejar estado 'delivered' como filtro especial de en_movimiento
      if (filterParams.status && filterParams.status.includes('delivered')) {
        filterParams.en_movimiento = true
        filterParams.status = filterParams.status.filter(s => s !== 'delivered')
        // Si status quedó vacío después de filtrar, eliminarlo
        if (filterParams.status && filterParams.status.length === 0) {
          delete filterParams.status
        }
      } else if (filterParams.status && filterParams.status.includes('registered')) {
        // Si solo hay 'registered' sin 'delivered', enviar en_movimiento = false
        filterParams.en_movimiento = false
      }
      
    // Manejar filtro de dealer según acceso del usuario
    if (!this.$store.getters.HAS_ROLE(['colaborator']) && !this.$store.getters.IS_PARTNER) {
      if (this.filter.dealer_id) {
        // El usuario seleccionó un dealer manualmente → filtra por ese dealer
        filterParams.colaborador_dealer_filter = this.filter.dealer_id
        delete filterParams.dealer_id
      } else if (this.accessibleDealers.length > 1) {
        // Tiene múltiples dealers accesibles → enviar todos los IDs para que el backend los incluya
        filterParams.colaborador_dealer_filter = this.accessibleDealers.map(d => d.id)
        delete filterParams.dealer_id
      } else {
        // Solo tiene su propio dealer → restringir al dealer propio
        const userDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
        if (userDealerId) {
          filterParams.colaborador_dealer_filter = userDealerId
        }
      }
    } else if (this.filter.dealer_id) {
      // Es partner/colaborator con filtro manual
      filterParams.colaborador_dealer_filter = this.filter.dealer_id
      delete filterParams.dealer_id
    }
      
      // Log para debugging del ordenamiento
      // Asegurar que siempre se incluya el expand para dealer
      // filterParams.expand = 'dealer'
      
      // Cargar datos normalmente (los filtros en_movimiento y en_cesion ya están en filterParams)
      this.$store.dispatch('LOAD_VEHICULOS', filterParams)
        .then(result => {
          this.totalItems = result
          // Asegurarse de que la página actual es válida
          const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
          if (this.pagination.page > maxPage && maxPage > 0) {
            this.pagination.page = maxPage
          }
          this.loadingItems = false
        })
        .catch(error => {
          console.error('Error al cargar vehículos:', error)
          this.$toast.error('Error al cargar los vehículos')
          this.loadingItems = false
        })
    },
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 500),
    async loadStats(filterParams = null) {
      try {
        // Si no se pasan parámetros de filtro, usar los filtros actuales
        const statsParams = filterParams || this.buildFilterParams()
        
        // Transformar los parámetros al formato filter[key] para el API
        let formattedParams = {}
        for (let key in statsParams) {
          if (statsParams.hasOwnProperty(key)) {
            formattedParams[`filter[${key}]`] = statsParams[key]
          }
        }
        
        const stats = await VehiculoService.getStats(formattedParams)
        
        this.vehiculosTotales = stats.data.data.total_vehicles || 0
        this.vehiculosPendientes = stats.data.data.pending_vehicles || 0
        this.vehiculosCreados = stats.data.data.created_vehicles || 0
        this.vehiculosRecepcionados = stats.data.data.recepcionados_vehicles || 0
        this.vehiculosEnMovimiento = stats.data.data.in_movement_vehicles || 0
        this.vehiculosLibres = stats.data.data.free_vehicles || 0
        this.vehiculosCreadosEsteMes = stats.data.data.created_this_month || 0
        
        // Forzar actualización de la UI
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      } catch (error) {
        this.$toast.error('Error al cargar las estadísticas de vehículos')
      }
    },
    buildFilterParams() {
      // Construir parámetros de filtro de la misma manera que en loadData
      let filterParams = { ...this.filter }
      
      // Agregar type=vehicle para estadísticas según la documentación del API
      // Nota: El type va en la URL (/stats/vehicle), no como parámetro
      // filterParams.type = 'vehicle'
      
      // Limpiar arrays vacíos
      const arrayFields = ['location_id', 'status', 'todo_ids', 'estado_proceso_id', 'condition']
      arrayFields.forEach(field => {
        if (Array.isArray(filterParams[field])) {
          if (!filterParams[field].length) {
            delete filterParams[field]
          }
        }
      })
      
      // Manejar en_movimiento y en_cesion
      if (this.filter.en_movimiento) {
        filterParams.en_movimiento = 1
      }
      if (this.filter.en_cesion) {
        filterParams.en_cesion = 1
      }
      
      // Manejar rango de fechas
      if (this.dateRange && this.dateRange.length === 2) {
        // Convertir las fechas a timestamps Unix
        const sortedDates = [...this.dateRange].sort()
        const startDate = new Date(sortedDates[0])
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(sortedDates[1])
        endDate.setHours(23, 59, 59, 999)
        
        filterParams.created_at_from = Math.floor(startDate.getTime() / 1000)
        filterParams.created_at_to = Math.floor(endDate.getTime() / 1000)
      }
      
      // Manejar estado 'delivered' como filtro especial de en_movimiento
      if (filterParams.status && filterParams.status.includes('delivered')) {
        filterParams.en_movimiento = true
        filterParams.status = filterParams.status.filter(s => s !== 'delivered')
        // Si status quedó vacío después de filtrar, eliminarlo
        if (filterParams.status && filterParams.status.length === 0) {
          delete filterParams.status
        }
      } else if (filterParams.status && filterParams.status.includes('registered')) {
        // Si solo hay 'registered' sin 'delivered', enviar en_movimiento = false
        filterParams.en_movimiento = false
      }
      
      // Manejar filtro de dealer según acceso del usuario
      if (this.filter.dealer_id) {
        filterParams.colaborador_dealer_filter = this.filter.dealer_id
        delete filterParams.dealer_id
      } else if (!this.$store.getters.HAS_ROLE(['colaborator']) && !this.$store.getters.IS_PARTNER && this.accessibleDealers.length > 1) {
        // Tiene múltiples dealers accesibles → enviar todos los IDs
        filterParams.colaborador_dealer_filter = this.accessibleDealers.map(d => d.id)
        delete filterParams.dealer_id
      } else if (!this.$store.getters.HAS_ROLE(['colaborator']) && !this.$store.getters.IS_PARTNER) {
        // Solo tiene su propio dealer → restringir al dealer propio
        const userDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
        if (userDealerId) {
          filterParams.colaborador_dealer_filter = userDealerId
        }
      }
      
      return filterParams
    },
    refreshDataTable () {
      this.tableId++
      this.loadData()
      this.loadStats()
    },
    async loadLastMovement (item) {
      try {
        this.$set(this.loadingMovements, item.id, true)
        // Si ya tenemos los movimientos en el item
        if (item.movements && item.movements.length > 0) {
          try {
            // Ordenamos por fecha descendente y tomamos el primero
            const latestMovement = [...item.movements].sort((a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
            )
            const [sourceResponse, destinationResponse] = await Promise.all([
              LocationService.view({ id: latestMovement[0].source_location_id }),
              LocationService.view({ id: latestMovement[0].destination_location_id })
            ])
            this.sourceLocation = sourceResponse.data.data.name || 'No especificado'
            this.destinationLocation = destinationResponse.data.data.name || 'No especificado'
            // Cargar ubicaciones y conductor en paralelo
            if (latestMovement[0].driver_id !== null) {
              const [driverResponse] = await Promise.all([
                DriverService.view({ id: latestMovement[0].driver_id })
              ])
              
              this.driver = 'Driver: ' + driverResponse.data.data.name || 'No especificado'
            } else if (latestMovement[0].crane_id !== null) {
              const [sourceResponse, destinationResponse, gruaResponse] = await Promise.all([
                LocationService.view({ id: latestMovement[0].source_location_id }),
                LocationService.view({ id: latestMovement[0].destination_location_id }),
                GruaService.view({ id: latestMovement[0].crane_id })
              ])
              
              // Asignar datos obtenidos
              this.sourceLocation = sourceResponse.data.data.name || 'No especificado'
              this.destinationLocation = destinationResponse.data.data.name || 'No especificado'
              this.driver = 'Grua: ' + gruaResponse.data.data.company || 'No especificado'
            } else if (latestMovement[0].transportation === 'YO MISMO') {
              const [gruaResponse] = await Promise.all([
                UserService.view({ id: latestMovement[0].responsible_id })
              ])
              this.driver = 'Driver: ' + gruaResponse.data.data.name || 'No especificado'
            }

            // Establecer el último movimiento en el item
            this.$set(item, 'last_movement', latestMovement[0])
          } catch (error) {
            this.$set(item, 'last_movement', {
              error: 'Error al cargar los datos del movimiento'
            })
          }
        } else {
          this.$set(item, 'last_movement', null)
        }
      } catch (error) {
        this.$set(item, 'last_movement', {
          error: 'Error al procesar los movimientos'
        })
      } finally {
        this.$set(this.loadingMovements, item.id, false)
      }
    },
    onItemExpanded ({ item, value }) {
      if (value) {
        // Verificar si ya tenemos los datos o están cargando
        const hasData = item.last_movement !== undefined
        const isLoading = this.loadingMovements[item.id]
        // Solo cargar datos si no los tenemos y no están en proceso de carga
        if (!hasData && !isLoading) {
          this.loadLastMovement(item)
        }
      }
    },

    // Filtros
    commitFilter () {
      for (const key in this.currentFilter) {
        this.currentFilter[key] = this.filter[key]
      }
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },
    rollbackFilter () {
      for (const key in this.currentFilter) {
        this.filter[key] = this.currentFilter[key]
      }
      this.loadStats(this.buildFilterParams())
    },
    applyQuickFilter(index) {
      if (index === null) {
        this.filter.status = []
        this.filter.en_movimiento = false
        this.filter.en_cesion = false
      } else if (index === 3) {
        // Filtro para 'En movimiento'
        this.filter.status = []
        this.filter.en_movimiento = true
        this.filter.en_cesion = false
      } else if (index === 4) {
        // Filtro para 'En cesión'
        this.filter.status = []
        this.filter.en_movimiento = false
        this.filter.en_cesion = true
      } else if (index === 5) {
        // Filtro para vehículos libres/archivados
        this.filter.status = ['free']
        this.filter.en_movimiento = false
        this.filter.en_cesion = false
      } else {
        const statusValues = ['pending', 'created', 'registered', 'free']
        this.filter.status = [statusValues[index]]
        this.filter.en_movimiento = false
        this.filter.en_cesion = false
      }
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },
    applyConditionFilter(condition) {
      // Si el array no está inicializado, inicializarlo
      if (!Array.isArray(this.filter.condition)) {
        this.filter.condition = []
      }
      
      // Si la condición ya está en el array, quitarla
      const index = this.filter.condition.indexOf(condition)
      if (index > -1) {
        this.filter.condition.splice(index, 1)
      } else {
        // Si no está, agregarla
        this.filter.condition.push(condition)
      }
      
      this.commitFilter()
    },
    resetFilters () {
      // Usar la mutación del store para resetear los filtros
      this.$store.commit('RESET_VEHICULOS_FILTERS')
      // Limpiar también el rango de fechas
      this.dateRange = []
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },

    applyFilter () {
      this.debouncedLoadData()
      this.loadStats(this.buildFilterParams())
    },

    // Método para manejar cambios en el rango de fechas
    onDateRangeChange (value) {
      if (value && value.length === 2) {
        this.dateRange = value
        this.datePickerMenu = false
        // Aplicar el filtro automáticamente cuando cambia el rango
        this.loadData()
        this.loadStats(this.buildFilterParams())
      }
    },
    
    // Limpiar el rango de fechas
    clearDateRange() {
      this.dateRange = []
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },

    // Acciones
    async showVehiculoForm (item) {
      // verificar si puede seguir anadiendo vehiculos
      /* await UserService.getMe().then(response => {
        const user = response.data.data
        if (user.dealer.balance.available_amount > 0 && user.dealer.balance.available_amount < user.dealer.balance.current_amount) {
          this.$toast.error('Ya no puedes crear más vehículos, actualice su plan')
        } else {
          this.editItem = item ? { ...item } : null
          this.visibleVehiculoForm = true
        }
      }).catch(error => {
      }) */
      this.editItem = item ? { ...item } : null
      this.visibleVehiculoForm = true
    },
    updateDataTable ({ insert }) {
      this.visibleVehiculoForm = false
      if (insert) {
        this.totalItems++
      }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Vehículo creado con éxito' : 'Vehículo actualizado con éxito')
    },
    confirmDeleteVehiculo (item) {
      this.selectedVehiculo = item
      this.dialogDelete = true
    },
    deleteVehiculo () {
      this.loadingDelete = true
      this.$store.dispatch('DELETE_VEHICULOS', this.selectedVehiculo).then(() => {
        this.dialogDelete = false
        this.loadingDelete = false
        this.$toast.success('Vehículo eliminado con éxito')
        this.refreshDataTable()
      }).catch(error => {
        console.error('Error al eliminar vehículo:', error)
        this.$toast.error('Error al eliminar el vehículo')
        this.loadingDelete = false
      })
    },
    
    // Métodos para el diálogo de estado de proceso
    openEstadoProcesoDialog(vehicle) {
      this.selectedVehicleForEstado = vehicle
      this.estadoProcesoDialogVisible = true
    },
    
    onEstadoProcesoUpdated(data) {
      // Cerrar el diálogo
      this.estadoProcesoDialogVisible = false
      this.selectedVehicleForEstado = null
      
      // Recargar la lista completa de vehículos para asegurar consistencia
      this.refreshDataTable()
      
      // Mostrar mensaje de éxito
      this.$toast.success('Estado de proceso actualizado correctamente')
    },
    
    toggleViewMode () {
      // Cambiar entre table y cards usando el setter de la computed property
      this.viewMode = this.viewMode === 'table' ? 'cards' : 'table'
      
      // Desactivar la carga automática durante el cambio de vista
      const previousMode = this.viewMode
      // Si estamos cambiando de cards a table, necesitamos un enfoque especial
      if (previousMode === 'cards' && this.viewMode === 'table') {
        // Deshabilitar temporalmente los watchers
        this.$nextTick(() => {
          // Incrementar tableId para asegurar que la tabla se re-renderice correctamente
          this.tableId++
          // Usar setTimeout para asegurar que todo se ha renderizado correctamente
          // antes de volver a habilitar la carga
          setTimeout(() => {
            // No hacemos nada más, permitimos que la interfaz se estabilice
          }, 100)
        })
      }
    },
    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      }
    },
    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },
    // Utilidades
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    handlePaginationChange () {
      // Evitar llamadas múltiples durante la inicialización
      if (!this.isInitializing) {
        this.loadData()
      }
    },
    // La API expone el flag como booleano `exceptional_movement`, pero al ser
    // un tinyint puede llegar como 0/1 (o como true/false tras un PUT), así que
    // se normaliza igual que en TransportForm/MovementProcessingView.
    isExcepcional (vehicle) {
      if (!vehicle) return false
      const value = vehicle.exceptional_movement
      return value === true || value === 1 || value === '1'
    },
    getStatusColor(status) {
      // Si recibe un objeto vehículo, usar lógica extendida
      if (typeof status === 'object' && status !== null) {
        const item = status
        // Vehículo archivado por movimiento excepcional: confiar el status
        // "efectivo" que ya devuelve la API según el dealer logueado
        // (Archivado para el dueño, Recepcionado para el dealer del grupo).
        if (this.isExcepcional(item)) {
          const effectiveColors = {
            'registered': 'green',
            'free': 'teal'
          }
          return effectiveColors[item.status] || 'teal'
        }
        let lastMovement = null
        if (item.movements && item.movements.length > 0) {
          lastMovement = [...item.movements].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
        }
        if (lastMovement) {
          if ((lastMovement.type === 'move' && lastMovement.status === 'processing') || (lastMovement.type === 'assignment' && lastMovement.status === 'processing')) {
            return 'purple'
          }
        }
        status = item.status
      }
      const colors = {
        'pending': 'orange',
        'created': 'blue',
        'registered': 'green',
        'delivered': 'purple',
        'free': 'teal'
      }
      return colors[status] || 'grey'
    },
    getStatusText(item) {
      if (!item) return 'Desconocido'
      // Vehículo archivado por movimiento excepcional: confiar el status
      // "efectivo" que ya devuelve la API según el dealer logueado.
      // Al poner el status efectivo primero evitamos que un movimiento
      // excepcional en processing se muestre como "En movimiento" para el dueño.
      if (this.isExcepcional(item)) {
        const effectiveTexts = {
          'registered': 'Recepcionado',
          'free': 'Archivados'
        }
        return effectiveTexts[item.status] || 'Archivados'
      }
      let lastMovement = null
      if (item.movements && item.movements.length > 0) {
        lastMovement = [...item.movements].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      }
      if (lastMovement) {
        if (lastMovement.type === 'move' && lastMovement.status === 'processing') return 'En movimiento'
        if (lastMovement.type === 'assignment' && lastMovement.status === 'processing') return 'En cesión'
      }
      // Solo mostrar 'Recepcionado' si el último movimiento es 'entry' o 'exit' y status 'completed'
      if (item.status === 'registered') {
        if (lastMovement &&
          ((lastMovement.type === 'entry' && lastMovement.status === 'completed') ||
           (lastMovement.type === 'exit' && lastMovement.status === 'completed') || (lastMovement.type === 'assignment' && lastMovement.status === 'completed') ||
          (lastMovement.type === 'assignment' && lastMovement.status !== 'processing') || (lastMovement.type === 'move' && lastMovement.status !== 'processing'))
        ) {
          return 'Recepcionado'
        } else {
          return 'Desconocido'
        }
      }
      const statusTexts = {
        'pending': 'Sin Bastidor',
        'created': 'Sin Ubicación',
        'delivered': 'Entregado',
        'free': 'Archivados'
      }
      return item.status ? statusTexts[item.status] || 'Desconocido' : statusTexts[item]
    },
    openTransportForm (vehicle) {
      this.selectedVehicleForTransport = vehicle
      this.isMovementMode = true
      this.isAssignmentMode = false
      this.isRecepcionarMode = false
      this.transportFormVisible = true
    },
    openTransportFormRecepcionar (vehicle) {
      this.selectedVehicleForTransport = vehicle
      this.isRecepcionarMode = true
      this.isMovementMode = false
      this.isAssignmentMode = false
      this.transportFormVisible = true
    },
    openAssignmentForm (vehicle) {
      this.selectedVehicleForTransport = vehicle
      this.isAssignmentMode = true
      this.isMovementMode = false
      this.isRecepcionarMode = false
      this.transportFormVisible = true
    },
    // Método para manejar el cierre del formulario de transporte
    closeTransportForm () {
      this.transportFormVisible = false
      this.selectedVehicleForTransport = null
      this.isRecepcionarMode = false
      this.isAssignmentMode = false
      this.isMovementMode = false
    },
    // Método para manejar el envío exitoso del formulario de transporte
    handleTransportSubmit (data) {
      this.transportFormVisible = false
      this.selectedVehicleForTransport = null
      this.isRecepcionarMode = false
      this.isAssignmentMode = false
      this.isMovementMode = false
      // Recargar datos para reflejar el nuevo movimiento
      this.refreshDataTable()
      this.$toast.success('Petición de movimiento creada con éxito')
    },
    canMov (vehicle) {
      if (!vehicle) return false
      // Se puede crear movimiento si está en estado 'registered'
      return vehicle.status === 'registered'
    },
    canRecepcionar (vehicle) {
      if (!vehicle) return false
      // Vehículo archivado por movimiento excepcional: el dueño no debe
      // recepcionarlo en una ubicación propia mientras esté fuera.
      if (this.isExcepcional(vehicle)) return false
      // Se puede recepcionar si está en estado 'created' o 'free' (liberado)
      return vehicle.status === 'created' || vehicle.status === 'free'
    },
    canCesion (vehicle) {
      if (!vehicle || vehicle.status === 'free') return false
      // Se puede cesionar si está en estado 'registered'
      return vehicle.status === 'registered'
    },

    async downloadExcelTemplate() {
      try {
        this.loadingTemplate = true
        
        const response = await VehiculoService.downloadTemplate()
        
        // El response.data ya es un Blob con el tipo correcto
        const blob = response.data
        
        // Verificar que el blob es válido
        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }
        
        // Crear URL temporal para el blob
        const url = window.URL.createObjectURL(blob)
        
        // Crear elemento de enlace temporal
        const link = document.createElement('a')
        link.href = url
        link.download = 'plantilla_vehiculos.xlsx'
        link.style.display = 'none'
        
        // Agregar al DOM, hacer click y limpiar inmediatamente
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Limpiar URL
        window.URL.revokeObjectURL(url)
        
        this.$toast.success('Plantilla descargada correctamente')
      } catch (error) {
        this.$toast.error('Error al descargar la plantilla: ' + (error.message || 'Error desconocido'))
      } finally {
        this.loadingTemplate = false
      }
    },
    async exportVehicles() {
      try {
        this.loadingExport = true

        const response = await VehiculoService.exportVehicles()
        
        // El response.data ya es un Blob con el tipo correcto
        const blob = response.data
        
        // Verificar que el blob es válido
        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }
        
        // Crear URL temporal para el blob
        const url = window.URL.createObjectURL(blob)
        
        // Crear elemento de enlace temporal
        const link = document.createElement('a')
        link.href = url
        link.download = 'vehiculos_exportados.xlsx'
        link.style.display = 'none'
        
        // Agregar al DOM, hacer click y limpiar inmediatamente
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Limpiar URL
        window.URL.revokeObjectURL(url)

        this.$toast.success('Vehículos exportados correctamente')
        this.loadingExport = false
      } catch (error) {
        this.$toast.error('Error al descargar la plantilla: ' + (error.message || 'Error desconocido'))
      } finally {
        this.loadingTemplate = false
      }
    },

    // Métodos para importación de Excel
    openImportDialog() {
      this.importDialog = true
      this.importFile = null
      this.importResult = null
    },

    closeImportDialog() {
      this.importDialog = false
      this.importFile = null
      this.importResult = null
      // Recargar datos si hubo importaciones exitosas
      if (this.importResult && this.importResult.summary.success_count > 0) {
        this.refreshDataTable()
      }
    },

    async importExcel() {
      if (!this.importFile) {
        this.$toast.error('Selecciona un archivo Excel primero')
        return
      }

      try {
        this.loadingImport = true
        
        // Crear FormData con el archivo
        const formData = new FormData()
        formData.append('excel_file', this.importFile)
        
        const response = await VehiculoService.importExcel(formData)
        
        if (response.data && response.data.data) {
          this.importResult = response.data.data
          
          // Mostrar mensaje apropiado
          if (this.importResult.summary.error_count === 0) {
            this.$toast.success(`Importación completada: ${this.importResult.summary.success_count} vehículos importados exitosamente`)
          } else if (this.importResult.summary.success_count > 0) {
            this.$toast.warning(`Importación parcial: ${this.importResult.summary.success_count} exitosos, ${this.importResult.summary.error_count} con errores`)
          } else {
            this.$toast.error(`Importación fallida: ${this.importResult.summary.error_count} errores encontrados`)
          }

          // Recargar datos si hubo al menos una importación exitosa
          if (this.importResult.summary.success_count > 0) {
            this.refreshDataTable()
          }
        } else {
          throw new Error('Respuesta del servidor inválida')
        }
      } catch (error) {
        this.$toast.error('Error al procesar el archivo Excel: ' + (error.response?.data?.message || error.message || 'Error desconocido'))
      } finally {
        this.loadingImport = false
      }
    },

    async downloadImportResult() {
      if (!this.importResult || !this.importResult.download_url) {
        this.$toast.error('No hay archivo de resultado disponible')
        return
      }

      try {
        this.loadingTemplate = true
        
        // Usar la URL directa que viene del servidor
        const response = await VehiculoService.downloadImportResult(this.importResult.download_url)
        
        // El response.data es un Blob con el archivo Excel
        const blob = response.data
        
        if (!blob || blob.size === 0) {
          throw new Error('El archivo de resultado está vacío')
        }
        
        // Crear URL para descarga
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // Extraer nombre del archivo desde la URL o generar uno con timestamp
        let fileName = 'resultado_importacion.xlsx'
        try {
          const urlPath = new URL(this.importResult.download_url).pathname
          const pathSegments = urlPath.split('/')
          const lastSegment = pathSegments[pathSegments.length - 1]
          if (lastSegment && lastSegment.includes('.xlsx')) {
            fileName = lastSegment
          }
        } catch (urlError) {
          // Usar nombre por defecto
        }
        
        link.download = fileName
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$toast.success('Archivo de resultado descargado correctamente')
      } catch (error) {
        this.$toast.error('Error al descargar el archivo de resultado: ' + (error.message || 'Error desconocido'))
      } finally {
        this.loadingTemplate = false
      }
    }
  },

  watch: {
    pagination: {
      handler (newVal, oldVal) {
        // Interceptar cuando se ordena por condición y limpiar otros ordenamientos
        if (newVal.sortBy && newVal.sortBy.length > 0) {
          const lastSortField = newVal.sortBy[newVal.sortBy.length - 1]
          
          // Si el último campo ordenado es 'condition' y hay más de un ordenamiento
          if (lastSortField === 'condition' && newVal.sortBy.length > 1) {
            // Limpiar todos los ordenamientos excepto 'condition'
            const conditionIndex = newVal.sortBy.lastIndexOf('condition')
            this.pagination.sortBy = ['condition']
            this.pagination.sortDesc = [newVal.sortDesc[conditionIndex]]
            
            return // Salir sin recargar, el watcher se disparará de nuevo con los valores limpios
          }
        }
        
        // Verificar si realmente cambió algo importante que requiera recargar
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        // Solo recargar si hay cambios relevantes y no estamos en medio de una carga
        if (relevantPropsChanged && !this.loadingItems && !this.isInitializing) {
          this.loadData()
        }
      },
      deep: true
    },
    searchTerm () {
      this.debouncedLoadData()
    },
    // Agregar este nuevo watcher
    viewMode (newVal) {
      // Recargar datos cuando cambiamos de vista
      if (!this.isInitializing) {
        // Dar tiempo a la UI para actualizarse antes de cargar datos
        this.$nextTick(() => {
          this.loadData()
        })
      }
    },
    manualViewMode (newVal, oldVal) {
      // Incrementar tableId cuando cambiamos a vista de tabla para forzar re-renderizado
      if (newVal === 'table' && oldVal === 'cards') {
        this.$nextTick(() => {
          this.tableId++
          setTimeout(() => {}, 100)
        })
      }
    }
  },

  async created () {
    // Bandera para evitar recarga durante la inicialización
    this.isInitializing = true
    
    // Inicializar currentFilter con los valores del store
    const storeFilters = this.$store.getters.VEHICULOS_FILTERS
    for (const key in this.currentFilter) {
      if (storeFilters.hasOwnProperty(key)) {
        this.currentFilter[key] = Array.isArray(storeFilters[key]) 
          ? [...storeFilters[key]] 
          : storeFilters[key]
      }
    }
    
    // Cargar dealers accesibles si es colaborador
    await this.loadAccessibleDealers()
    // Cargar ubicaciones
    await this.loadLocations()
    // Cargar TO-DO
    await this.loadTodos()
    // Cargar estados de proceso
    await this.loadEstadosProceso()

    // Cargar estadísticas
    await this.loadStats()
    
    VehiculoService.get()
      .then(response => {
        // const vehiculos = response.data.data
        // this.vehiculosTotales = response.data._meta.totalCount
        // this.vehiculosPendientes = vehiculos.filter(v => v.status === 'pending').length

        /* UserService.getMe().then(response => {
          const user = response.data.data
          if (user.dealer) {
            if (user.dealer.balance !== null) {
              this.vehiculosDisponibles = user.dealer.balance.available_amount === -1 ? 'Ilimitado' : user.dealer.balance.available_amount - user.dealer.balance.current_amount
            }
          } else {
            this.vehiculosDisponibles = 'Ilimitado'
          }
        })
          .catch(error => {
            this.$toast.error('Error al cargar el saldo del usuario')
          }) */

        // Cargar datos con paginación
        this.loadData()

        // Desactivar la bandera de inicialización después de la carga inicial
        this.$nextTick(() => {
          this.isInitializing = false
        })
      })
      .catch(error => {
        console.error('Error al obtener los vehículos:', error)
        this.$toast.error('Error al cargar los vehículos')
        this.isInitializing = false
      })
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.8;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

.vehicle-card {
  border-radius: 8px;
  overflow: hidden;
}

.vehicle-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  border-radius: 8px;
}

.vehicle-item-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.stat-card {
  border-radius: 8px;
  transition: all 0.3s;
  /* Force square cards - compact size */
  width: 110px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
}

.stat-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-3px);
}

.stat-card .v-card__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px !important;
  box-sizing: border-box;
}

.stat-card .text-h6 {
  line-height: 1;
  font-size: 1.25rem !important;
  margin: 2px 0;
}

.stat-card .text-caption {
  line-height: 1.1;
  font-size: 0.85rem !important;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}

.search-input-mobile {
  max-width: 200px;
}

.h-100 {
  height: 100%;
}
</style>
